import { CopyOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import AdvanceVerfication from '../../assets/arts/AdvanceVerfication.svg';
import BasicVerfication from '../../assets/arts/BasicVerfication.svg';
import HiveVerfication from '../../assets/arts/new_arts/3 dots.svg';
import { decodeJWT, getUserDetails, sendOtp } from '../../services/api';
import { getKycStatus } from '../../services/kycApi';
import { getKybStatus } from '../../services/kybApi';
import useCopyToClipboard from '../../utils/useCopyToClipboard';
import OpenNotification from '../OpenNotification/OpenNotification';
import { useNavigate } from 'react-router-dom';
import GenericButton from '../updated/shared/Button';
import VerificationTypeSelection from './VerificationTypeSelection';
import './Account.css';
import IdentificationInput from './SSNInputField';
const BasicInfo = ({ theme }: { theme: any }) => {
  const [email, setEmail] = useState('');
  const [userData, setUserData] = useState() as any;
  const [kycStatus, setKycStatus] = useState<{ status?: string } | null>(null);
  const [kybStatus, setKybStatus] = useState<{ application?: { status?: string } } | null>(null);
  const [copiedValue, copy] = useCopyToClipboard();
  const [loadings, setLoadings] = useState(false);
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false); // Loader state
  const [userType, setUserType] = useState<string | null>(null); // Track userType for re-rendering

  const handleLoading = (state: boolean) => {
    setIsUpdating(state);
  };

  const handleSSNSuccess = async () => {
    await refreshUserData();
  };

  const checkKycStatus = async () => {
    try {
      const kycStatusRes = await getKycStatus();
      // Handle nested response structure (same pattern as VerifyWithIndexx)
      const status = (kycStatusRes as any).data?.data || (kycStatusRes as any).data;
      if (status) {
        setKycStatus(status);
        console.log('✅ KYC Status:', status.status);
        return status;
      }
    } catch (kycError: any) {
      // KYC status endpoint might not exist or return 404 - that's okay
      if (kycError.response?.status !== 404) {
        console.log('Could not check KYC status:', kycError);
      }
      setKycStatus(null);
    }
    return null;
  };

  const checkKybStatus = async () => {
    try {
      const kybStatusRes = await getKybStatus();
      // Handle nested response structure
      const status = (kybStatusRes as any).data?.data || (kybStatusRes as any).data;
      if (status) {
        setKybStatus(status);
        console.log('✅ KYB Status:', status.application?.status);
        return status;
      }
    } catch (kybError: any) {
      // KYB status endpoint might not exist or return 404 - that's okay
      if (kybError.response?.status !== 404) {
        console.log('Could not check KYB status:', kybError);
      }
      setKybStatus(null);
    }
    return null;
  };

  const refreshUserData = async () => {
    let access_token = String(localStorage.getItem('access_token'));
    let decoded: any = decodeJWT(access_token);

    setEmail(decoded.email);

    // Update userType state from localStorage
    const currentUserType = localStorage.getItem('userType');
    if (currentUserType !== userType) {
      setUserType(currentUserType);
    }

    try {
      const res = await getUserDetails(decoded.email);
      if (res.status === 200) {
        setUserData(res.data);
      }

      // Also check KYC and KYB status directly - this will show green circle even if isKYCPass is not updated
      await checkKycStatus();
      await checkKybStatus();
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  // Apply theme class based on userType and listen for changes
  useEffect(() => {
    const applyTheme = () => {
      const currentUserType = localStorage.getItem('userType');
      const userLogged = localStorage.getItem('userlogged');

      // Update state to trigger re-render only if changed
      setUserType(prev => {
        if (prev !== currentUserType) {
          return currentUserType;
        }
        return prev;
      });

      // Determine theme class based on userType or userLogged
      let themeClass = 'theme-normal'; // Default to green

      if (currentUserType && currentUserType !== 'Indexx Exchange') {
        // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
        themeClass = 'theme-captain'; // Yellow for CaptainBee
      } else if (currentUserType === 'CaptainBee' || userLogged === 'captain') {
        themeClass = 'theme-captain'; // Yellow for CaptainBee
      } else if (currentUserType === 'HoneyUser' || userLogged === 'honeyb') {
        themeClass = 'theme-honeyb'; // Yellow for HoneyBee
      } else if (userLogged === 'normal' || currentUserType === 'Indexx Exchange') {
        themeClass = 'theme-normal'; // Green for normal users
      }

      // Apply theme class to document body and root
      document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      document.body.classList.add(themeClass);
      document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      document.documentElement.classList.add(themeClass);

      // Also apply to root element to ensure CSS variables are available
      const root = document.getElementById('root');
      if (root) {
        root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
        root.classList.add(themeClass);
      }
    };

    // Apply theme on mount
    applyTheme();

    // Listen for storage changes (when userType is updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userType' || e.key === 'userlogged') {
        applyTheme();
      }
    };

    // Listen for custom userType change event
    const handleUserTypeChange = () => {
      applyTheme();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userTypeChanged', handleUserTypeChange);

    // Poll for userType changes (in case localStorage is updated in same tab)
    let lastUserType = localStorage.getItem('userType');
    const pollInterval = setInterval(() => {
      const currentUserType = localStorage.getItem('userType');
      if (currentUserType !== lastUserType) {
        lastUserType = currentUserType;
        applyTheme();
      }
    }, 500); // Check every 500ms

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userTypeChanged', handleUserTypeChange);
      clearInterval(pollInterval);
    };
  }, []); // Empty dependency array - only run on mount

  useEffect(() => {
    refreshUserData();

    // Listen for KYC status updates
    const handleKycStatusUpdate = async () => {
      await refreshUserData();
      // Also check KYC status immediately when event is triggered
      await checkKycStatus();
    };

    // Listen for KYB status updates
    const handleKybStatusUpdate = async () => {
      await refreshUserData();
      // Also check KYB status immediately when event is triggered
      await checkKybStatus();
    };

    window.addEventListener('kycStatusUpdated', handleKycStatusUpdate);
    window.addEventListener('kybStatusUpdated', handleKybStatusUpdate);

    return () => {
      window.removeEventListener('kycStatusUpdated', handleKycStatusUpdate);
      window.removeEventListener('kybStatusUpdated', handleKybStatusUpdate);
    };
  }, [email]);

  // Poll for KYC/KYB status updates every 30 seconds if not approved
  useEffect(() => {
    const isKycApproved = userData?.isKYCPass || kycStatus?.status === 'approved';
    const isKybApproved = kybStatus?.application?.status === 'approved';
    const isAdvancedApproved = isKycApproved || isKybApproved;

    if (!isAdvancedApproved) {
      const pollInterval = setInterval(() => {
        refreshUserData();
      }, 30000); // Poll every 30 seconds

      return () => clearInterval(pollInterval);
    }
  }, [userData?.isKYCPass, kycStatus?.status, kybStatus?.application?.status, email]);

  const openBlockpassLink = async () => {
    // This function is no longer needed as we show selection directly
    // Keeping it for backward compatibility but it won't be called
  };

  const resendEmail = async () => {
    setLoadings(true);
    let res = await sendOtp(email);
    if (res.status === 200) {
      OpenNotification('success', res.data);
      setLoadings(false);
      navigate('email-auth');
    } else {
      setLoadings(false);
      OpenNotification('error', res.data);
    }
  };

  const renderVerificationStep = () => {
    // If userData is not loaded yet, show verification selection optimistically
    // This prevents delay in showing the UI while waiting for API response
    if (!userData) {
      // Show verification selection immediately - it will be updated once data loads
      const isKycApproved = kycStatus?.status === 'approved';
      if (isKycApproved) {
        return null;
      }
      return (
        <div className="col-lg-5 advanced_verification bs_main">
          <VerificationTypeSelection
            theme={theme}
          />
        </div>
      );
    }

    if (!userData?.verification?.emailVerified) {
      // Step 1: Show Basic Functions and Email Verification
      return (
        <div className="col-lg-5 basic_funtion bs_main">
          <h1
            className="font_18x margin-b-3x d-block font_weight_800 padding-l-24px"
            style={{ color: theme.palette.text.primary }}
          >
            Basic Functions
          </h1>
          <div className="usd_deposit">USD Deposits & Withdrawals,</div>
          <div className="padding-tb-1x crypto_deposit">
            Crypto Deposits & Withdrawals,
          </div>
          <div className="buy_Sell_convert">Buy, Sell & Convert</div>
          <div className="padding-tb-1x adv_trade">Advanced Trading</div>
          <div className="bank_trns_debit">Bank Transfer & Debit Card</div>
          <div className="padding-tb-1x staking">Staking</div>
          <GenericButton
            text={'Verify Email'}
            loading={loadings}
            onClick={() => resendEmail()}
            IconComponent={undefined}
            className={undefined}
            styles={undefined}
            disabled={undefined}
          />
        </div>
      );
    } else if (!userData?.personalIdNumber) {
      return (
        <div className="col-lg-5 bs_main">
          <h1
            className="font_18x margin-tb-2x d-block font_weight_800 "
            style={{ color: theme.palette.text.primary }}
          >
            SSN Verification
          </h1>
          <IdentificationInput
            initialCountry={userData?.country || 'US'}
            initialPersonalId={userData?.personalIdNumber || ''}
            setLoading={handleLoading}
            onSuccess={handleSSNSuccess}
          />
        </div>
      );
    } else {
      // Step 3: Show Advanced Verification
      const isKycApproved = userData?.isKYCPass || kycStatus?.status === 'approved';
      const isKybApproved = kybStatus?.application?.status === 'approved';
      const isAdvancedApproved = isKycApproved || isKybApproved;

      // If KYC or KYB is approved, don't show the Advanced Functions section
      if (isAdvancedApproved) {
        return null;
      }

      return (
        <div className="col-lg-5 advanced_verification bs_main">
          <VerificationTypeSelection
            theme={theme}
          />
        </div>
      );
    }
  };

  return (
    <div className={isUpdating ? 'blurred-screen' : ''}>
      <div className="basic_info container margin-t-2x padding-t-3x">
        <div>
          <header className="font_25x border-b-1x padding-lr-2x padding-tb-1x">
            Account Info
          </header>
          <div className="padding-2x">
            <p
              className="font_20x"
              style={{ color: theme.palette.text.primary }}
            >
              {userData?.email}
            </p>
            <div className="basic-det">
              <div>{userData?.vipLevel}</div>
              <div>Personal</div>
              <div>User ID</div>
              <div className=" d-flex align-items-center">
                <span>{String(userData?.email)}</span>
                <CopyOutlined
                  className="hover_icon"
                  onClick={() => copy(String(userData?.email))}
                />
                {/* <Tooltip title="Copied to Clipboard!" ></Tooltip> */}
              </div>
              <div>Referral Code</div>
              <div className="d-flex align-items-center">
                <span>{userData?.referralCode}</span>
                {/* <Tooltip title="Click to copy"><CopyOutlined className='padding-lr-1x hover_icon' /> </Tooltip> */}
                <CopyOutlined
                  className="hover_icon"
                  onClick={() => copy(userData?.referralCode)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="basic_info container margin-t-2x padding-t-3x margin-b-2x">
        <div>
          <header className="font_18x border-b-1x padding-lr-2x padding-tb-1x">
            Verification & Limits
          </header>
          <div className="padding-2x row">
            <div className="col-lg-5">
              <div className="verification-box">
                <div
                  className={
                    userData?.verification?.emailVerified
                      ? 'd-flex align-items-center  padding-1x'
                      : 'd-flex align-items-center padding-1x'
                  }
                  style={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                  }}
                >
                  {userData?.verification?.emailVerified ? (
                    <div className="d-flex align-items-center">
                      <img
                        src={
                          userType === 'CaptainBee' || localStorage.getItem('userType') === 'CaptainBee'
                            ? HiveVerfication
                            : BasicVerfication
                        }
                        alt="AdvanceVerfication"
                        className="font_30x margin-r-1x"
                        width={'40px'}
                      />
                      <h2 className="font_18x margin-b-0">
                        <span
                          style={{
                            color: `${theme.palette.text.primary} !important`,
                          }}
                        >
                          Basic Verification
                        </span>
                      </h2>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <img
                        src={AdvanceVerfication}
                        alt="AdvanceVerfication"
                        className="font_30x margin-r-1x"
                      />
                      <h2
                        className="font_18x margin-b-0"
                        style={{
                          minWidth: 'fit-content',
                          color: `${theme.palette.text.primary} !important`,
                        }}
                      >
                        <span
                          style={{
                            color: `${theme.palette.text.primary} !important`,
                          }}
                        >
                          Basic Verification
                        </span>
                      </h2>
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`verification-box margin-t-2x ${!userData?.verification?.emailVerified
                  ? 'disabled-verification'
                  : ''
                  }`}
              >
                <div
                  className="d-flex align-items-center padding-1x"
                  style={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                  }}
                >
                  <div>
                    <img
                      src={
                        userData?.personalIdNumber
                          ? (userType === 'CaptainBee' || localStorage.getItem('userType') === 'CaptainBee'
                            ? HiveVerfication
                            : BasicVerfication)
                          : AdvanceVerfication
                      }
                      alt="SSNVerification"
                      className="font_30x margin-r-1x"
                      width={'40px'}
                    />
                  </div>
                  <div>
                    <h2
                      className="font_18x margin-b-0"
                      style={{
                        color: `${theme.palette.text.primary} !important`,
                      }}
                    >
                      SSN Verification
                    </h2>
                    {!userData?.personalIdNumber &&
                      userData?.verification?.emailVerified && (
                        <div className="font_12x opacity-50">
                          Complete SSN verification to proceed with advanced
                          verification
                        </div>
                      )}
                  </div>
                </div>
              </div>

              <div
                className={`verification-box margin-t-2x ${!userData?.personalIdNumber ? 'disabled-verification' : ''
                  }`}
              >
                <div
                  className={
                    !userData?.isKYCPass && kycStatus?.status !== 'approved'
                      ? 'd-flex align-items-center  margin-t-2x padding-1x'
                      : 'd-flex align-items-center  padding-1x'
                  }
                  style={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                  }}
                >
                  {(() => {
                    const isKycApproved = userData?.isKYCPass || kycStatus?.status === 'approved';
                    const isKybApproved = kybStatus?.application?.status === 'approved';
                    const isAdvancedApproved = isKycApproved || isKybApproved;

                    return !isAdvancedApproved ? (
                      <div>
                        <img
                          src={AdvanceVerfication}
                          alt="AdvanceVerfication"
                          className="font_30x margin-r-1x"
                        />
                      </div>
                    ) : (
                      <div>
                        <img
                          src={
                            userType === 'CaptainBee' || localStorage.getItem('userType') === 'CaptainBee'
                              ? HiveVerfication
                              : BasicVerfication
                          }
                          alt="AdvanceVerfication"
                          className="font_30x margin-r-1x"
                          width={'40px'}
                        />
                      </div>
                    );
                  })()}
                  <div>
                    <h2
                      className="font_18x margin-b-0"
                      style={{
                        color: `${theme.palette.text.primary} !important`,
                      }}
                    >
                      Advanced Verification
                    </h2>
                    {(() => {
                      const isKycApproved = userData?.isKYCPass || kycStatus?.status === 'approved';
                      const isKybApproved = kybStatus?.application?.status === 'approved';
                      const isAdvancedApproved = isKycApproved || isKybApproved;

                      return !isAdvancedApproved && (
                        <div className="font_12x opacity-50">
                          Unlock further features and raise your deposit and
                          withdrawal limits{' '}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-2 mt-4"></div>
            {renderVerificationStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfo;
